import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, Button, Badge, Textarea, useToast } from '@hiring-intelligence/ui';
import { moduleApi } from '@/services/api';
import { formatDisplayNumber } from '@hiring-intelligence/shared';
import { History, RotateCcw, ChevronDown, ChevronUp, MessageSquare, Send, X, Download, Pen, RefreshCw } from 'lucide-react';
import { format } from 'date-fns';

/** Strip wrapping code fences (```markdown … ``` or ``` … ```) so the markdown renders properly */
function stripCodeFences(raw: string): string {
	const trimmed = raw.trim();
	const match = trimmed.match(/^```(?:\w+)?\s*\n([\s\S]*?)\n\s*```$/);
	return match ? match[1] : trimmed;
}

export type ModuleType =
	| 'company_research'
	| 'deal_briefing'
	| 'standard_jd'
	| 'blind_jd'
	| 'interview_questions'
	| 'candidate_sourcing';

export type ModuleSource = 'ai' | 'manual' | 'hybrid';

interface ModuleData {
	id?: string;
	moduleType: ModuleType;
	content: Record<string, unknown>;
	readableContent?: string;
	source: ModuleSource;
	version: number;
	additionalContext?: string | null;
	createdAt?: string;
	updatedAt?: string;
}

interface ModuleCardProps {
	hiringRequestId: string;
	moduleType: ModuleType;
	title: string;
	description: string;
	module?: ModuleData | null;
	isLoading?: boolean;
	meetingNotes?: string;
	onGenerate: (moduleType: ModuleType, additionalContext?: string, meetingNotes?: string) => Promise<void>;
	onSave: (moduleType: ModuleType, data: { content?: Record<string, unknown>; readableContent?: string }) => Promise<void>;
	onRefresh: () => void;
}

// Map module type to display title
const MODULE_TITLES: Record<ModuleType, string> = {
	company_research: 'Company Research',
	deal_briefing: 'Deal Briefing',
	standard_jd: 'Standard JD',
	blind_jd: 'Blind JD',
	interview_questions: 'Interview Questions',
	candidate_sourcing: 'Candidate Sourcing'
};

export function ModuleCard({
	hiringRequestId: _hiringRequestId,
	moduleType,
	title,
	description,
	module,
	isLoading,
	meetingNotes: initialMeetingNotes,
	onGenerate,
	onSave,
	onRefresh
}: ModuleCardProps) {
	const [isEditing, setIsEditing] = useState(false);
	const [editContent, setEditContent] = useState('');
	const [isGenerating, setIsGenerating] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [showHistory, setShowHistory] = useState(false);
	const [history, setHistory] = useState<any[]>([]);
	const [isLoadingHistory, setIsLoadingHistory] = useState(false);
	const [isExpanded, setIsExpanded] = useState(false);
	const [showContextInput, setShowContextInput] = useState(false);
	const [contextText, setContextText] = useState('');
	const [regenMeetingNotes, setRegenMeetingNotes] = useState('');
	const [isDownloadingPDF, setIsDownloadingPDF] = useState(false);
	const { toast } = useToast();

	const handleDownloadPDF = async () => {
		setIsDownloadingPDF(true);
		try {
			const blob = await moduleApi.downloadPDF(_hiringRequestId, moduleType);
			const url = URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = url;
			a.download = `${title.replace(/\s+/g, '-').toLowerCase()}-v${module?.version || 1}.pdf`;
			a.click();
			URL.revokeObjectURL(url);
		} catch (error) {
			toast({
				title: 'Download Failed',
				description: error instanceof Error ? error.message : 'Failed to download PDF',
				variant: 'destructive'
			});
		} finally {
			setIsDownloadingPDF(false);
		}
	};

	const fetchHistory = async () => {
		setIsLoadingHistory(true);
		try {
			const res = await moduleApi.history(_hiringRequestId, moduleType);
			setHistory(res.data?.editHistory || []);
		} catch (error) {
			console.error('Failed to fetch history:', error);
			toast({
				title: 'Error',
				description: 'Failed to load history',
				variant: 'destructive'
			});
		} finally {
			setIsLoadingHistory(false);
		}
	};

	const handleRestore = async (historyItem: any) => {
		try {
			// Restore content from history using dedicated API
			await moduleApi.restore(_hiringRequestId, moduleType, historyItem.version);

			setShowHistory(false);
			toast({
				title: 'Restored',
				description: `Restored version v${historyItem.version}`
			});
			onRefresh();
		} catch (error) {
			toast({
				title: 'Restore Failed',
				description: 'Failed to restore version',
				variant: 'destructive'
			});
		}
	};

	const hasContent = module && Object.keys(module.content).length > 0;
	const canDownloadPDF = (moduleType === 'standard_jd' || moduleType === 'blind_jd') && hasContent;

	const handleGenerate = async (context?: string) => {
		setIsGenerating(true);
		try {
			const notes = moduleType === 'deal_briefing' ? (regenMeetingNotes || initialMeetingNotes || undefined) : undefined;
			await onGenerate(moduleType, context || undefined, notes);
			toast({
				title: 'Generation Started',
				description: `${title} generation has been queued. Refresh to see results.`
			});
			setShowContextInput(false);
			setContextText('');
			setRegenMeetingNotes('');
		} catch (error) {
			toast({
				title: 'Generation Failed',
				description: error instanceof Error ? error.message : 'Unknown error',
				variant: 'destructive'
			});
		} finally {
			setIsGenerating(false);
		}
	};

	const handleEdit = () => {
		let contentToEdit = '';

		if (module?.readableContent) {
			contentToEdit = module.readableContent;
		} else {
			let jsonContent = module?.content || {};

			// Unwrap raw content if it exists and is parseable (legacy fallback)
			if ((jsonContent as any).rawContent && typeof (jsonContent as any).rawContent === 'string') {
				try {
					let cleanJson = (jsonContent as any).rawContent.trim();
					if (cleanJson.includes('```')) {
						cleanJson = cleanJson.replace(/```json\n?|\n?```/g, '').trim();
					}
					const firstBrace = cleanJson.indexOf('{');
					const lastBrace = cleanJson.lastIndexOf('}');
					if (firstBrace !== -1 && lastBrace !== -1) {
						cleanJson = cleanJson.substring(firstBrace, lastBrace + 1);
					}
					jsonContent = JSON.parse(cleanJson);
				} catch (e) {
					console.warn('Failed to unwrap rawContent for editing:', e);
				}
			}
			contentToEdit = JSON.stringify(jsonContent, null, 2);
		}

		setEditContent(contentToEdit);
		setIsEditing(true);
	};

	const handleSave = async () => {
		setIsSaving(true);
		try {
			// If we started with readableContent, we save as readableContent
			if (module?.readableContent) {
				await onSave(moduleType, {
					readableContent: editContent,
					// Preserve existing content structure
					content: module.content
				});
			} else {
				// Otherwise try to save as JSON
				const content = JSON.parse(editContent);
				await onSave(moduleType, { content });
			}

			setIsEditing(false);
			toast({
				title: 'Saved',
				description: 'Module content updated successfully.'
			});
			onRefresh();
		} catch (error) {
			toast({
				title: 'Save Failed',
				description: error instanceof Error ? error.message : 'Invalid JSON',
				variant: 'destructive'
			});
		} finally {
			setIsSaving(false);
		}
	};

	const handleCancel = () => {
		setIsEditing(false);
		setEditContent('');
	};

	const handleManualEntry = () => {
		let template = {};
		if (moduleType === 'candidate_sourcing') {
			template = {
				searchMetadata: { dealCode: '', client: '', role: '', totalSearches: 0 },
				sourcingStrategy: {
					ecosystemMapping: { description: '', industrySegments: [] },
					targetCompanies: {
						tier1: { description: 'Direct competitors', companies: [] },
						tier2: { description: 'Adjacent industry', companies: [] },
						tier3: { description: 'Transferable skillset', companies: [] }
					},
					targetPositionsAndKeywords: { targetTitles: [], coreKeywords: [], industryTerms: [] },
					linkedinSearches: [{ searchName: '', booleanQuery: '', purpose: '' }]
				},
				directHuntingSearches: [{
					searchNumber: 1, searchName: '', description: '', tier: 1,
					parameters: { location: '', jobTitle: '', currentPastCompany: '', totalExperience: '', keywords: '' }
				}],
				unconventionalSearches: [],
				executionTips: [],
				pipelineCleaningFilters: { excludeTitles: '', excludeCompanies: '' }
			};
		}

		setEditContent(JSON.stringify(template, null, 2));
		setIsEditing(true);
	};

	const renderContent = () => {
		if (!hasContent) {
			return (
				<div className="text-center py-8 text-muted-foreground flex flex-col items-center gap-4">
					<p>{description}</p>
					<div className="flex gap-4">
						<Button onClick={() => handleGenerate()} disabled={isGenerating || isLoading}>
							{isGenerating ? 'Generating...' : `Generate`}
						</Button>
						<Button variant="outline" onClick={handleManualEntry} disabled={isGenerating || isLoading}>
							Manual Entry
						</Button>
					</div>
				</div>
			);
		}

		if (isEditing) {
			return (
				<div className="space-y-4">
					<Textarea
						value={editContent}
						onChange={(e) => setEditContent(e.currentTarget.value)}
						className="min-h-[300px] font-mono text-sm"
					/>
					<div className="flex gap-2">
						<Button onClick={handleSave} disabled={isSaving}>
							{isSaving ? 'Saving...' : 'Save'}
						</Button>
						<Button variant="outline" onClick={handleCancel}>
							Cancel
						</Button>
					</div>
				</div>
			);
		}



		// Render content based on structure
		let content = module.content as any;

		// Prioritize readableContent (AI generated markdown)
		if (module.readableContent && typeof module.readableContent === 'string') {
			return (
				<div className="prose prose-sm max-w-none dark:prose-invert prose-table:w-full prose-table:border-collapse prose-th:border prose-th:border-border prose-th:px-3 prose-th:py-2 prose-th:bg-muted prose-th:text-left prose-td:border prose-td:border-border prose-td:px-3 prose-td:py-2">
					<ReactMarkdown remarkPlugins={[remarkGfm]}>{stripCodeFences(module.readableContent)}</ReactMarkdown>
				</div>
			);
		}

		// Improve parsing: If content has rawContent that is a string, parse it first so we can try to render specialized UI
		if (content.rawContent && typeof content.rawContent === 'string') {
			try {
				let cleanJson = content.rawContent.trim();
				if (cleanJson.includes('```')) {
					cleanJson = cleanJson.replace(/```json\n?|\n?```/g, '').trim();
				}
				const firstBrace = cleanJson.indexOf('{');
				const lastBrace = cleanJson.lastIndexOf('}');
				if (firstBrace !== -1 && lastBrace !== -1) {
					cleanJson = cleanJson.substring(firstBrace, lastBrace + 1);
				}
				const parsed = JSON.parse(cleanJson);
				// Merge parsed content so specialized renderers can see it
				content = { ...content, ...parsed };
			} catch (e) {
				// Ignore parse error, logic below will fall back to rawContent display
			}
		}

		// Handle Candidate Sourcing UI
		if (moduleType === 'candidate_sourcing' && !isEditing) {
			return (
				<div className="space-y-6">
					{/* Search Metadata */}
					{content.searchMetadata && (
						<div className="bg-muted/30 p-3 rounded-md text-sm">
							<div className="flex gap-4 flex-wrap">
								{content.searchMetadata.dealCode && <span><strong>Deal:</strong> {content.searchMetadata.dealCode}</span>}
								{content.searchMetadata.role && <span><strong>Role:</strong> {content.searchMetadata.role}</span>}
								{content.searchMetadata.totalSearches && <span><strong>Total Searches:</strong> {formatDisplayNumber(content.searchMetadata.totalSearches)}</span>}
							</div>
						</div>
					)}

					{/* Sourcing Strategy Overview */}
					{content.sourcingStrategy && (
						<div>
							<h3 className="text-sm font-semibold mb-3 text-primary">Sourcing Strategy</h3>
							<div className="space-y-3">
								{content.sourcingStrategy.ecosystemMapping && (
									<div className="bg-muted/30 p-3 rounded-md text-sm">
										<span className="font-medium">Ecosystem:</span> {content.sourcingStrategy.ecosystemMapping.description}
										{content.sourcingStrategy.ecosystemMapping.industrySegments?.length > 0 && (
											<div className="flex flex-wrap gap-1 mt-2">
												{content.sourcingStrategy.ecosystemMapping.industrySegments.map((s: string, i: number) => (
													<Badge key={i} variant="secondary" className="text-xs">{s}</Badge>
												))}
											</div>
										)}
									</div>
								)}
								{content.sourcingStrategy.targetCompanies && (
									<div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
										{['tier1', 'tier2', 'tier3'].map((tier) => {
											const t = content.sourcingStrategy.targetCompanies[tier];
											if (!t) return null;
											return (
												<div key={tier} className="bg-muted/30 p-3 rounded-md">
													<span className="font-medium text-xs block mb-1">{t.description || tier}</span>
													<div className="flex flex-wrap gap-1">
														{t.companies?.map((c: string, i: number) => (
															<Badge key={i} variant="outline" className="text-[10px]">{c}</Badge>
														))}
													</div>
												</div>
											);
										})}
									</div>
								)}
								{content.sourcingStrategy.linkedinSearches?.length > 0 && (
									<div>
										<span className="text-xs font-medium text-muted-foreground">LinkedIn Boolean Searches</span>
										<div className="space-y-2 mt-1">
											{content.sourcingStrategy.linkedinSearches.map((s: any, i: number) => (
												<div key={i} className="bg-muted p-2 rounded-md text-xs">
													<span className="font-medium">{s.searchName}:</span>
													<code className="block whitespace-pre-wrap font-mono mt-1">{s.booleanQuery}</code>
												</div>
											))}
										</div>
									</div>
								)}
							</div>
						</div>
					)}

					{/* Direct Hunting Searches */}
					{content.directHuntingSearches && content.directHuntingSearches.length > 0 && (
						<div>
							<h3 className="text-sm font-semibold mb-3 text-primary">Direct Hunting Searches (80%)</h3>
							<div className="space-y-3">
								{content.directHuntingSearches.map((search: any, i: number) => (
									<div key={i} className="border rounded-lg p-4 space-y-2">
										<div className="flex items-center gap-2">
											<Badge variant="default" className="text-xs">Tier {search.tier || i + 1}</Badge>
											<span className="font-medium text-sm">{search.searchName}</span>
										</div>
										<p className="text-xs text-muted-foreground">{search.description}</p>
										{search.parameters && (
											<div className="flex flex-wrap gap-1 mt-2">
												{Object.entries(search.parameters).map(([key, val]: [string, any]) => {
													if (!val || (typeof val === 'string' && !val.trim())) return null;
													const display = Array.isArray(val) ? val.join(', ') : String(val);
													return (
														<Badge key={key} variant="outline" className="text-[10px] font-normal">
															<span className="font-semibold">{key}:</span>&nbsp;{display.length > 60 ? display.slice(0, 60) + '...' : display}
														</Badge>
													);
												})}
											</div>
										)}
									</div>
								))}
							</div>
						</div>
					)}

					{/* Unconventional Searches */}
					{content.unconventionalSearches && content.unconventionalSearches.length > 0 && (
						<div>
							<h3 className="text-sm font-semibold mb-3 text-primary">Unconventional Searches (20%)</h3>
							<div className="space-y-3">
								{content.unconventionalSearches.map((search: any, i: number) => (
									<div key={i} className="border rounded-lg p-4 space-y-2 border-dashed">
										<div className="flex items-center gap-2">
											<Badge variant="secondary" className="text-xs">Alt {search.tier || i + 1}</Badge>
											<span className="font-medium text-sm">{search.searchName}</span>
										</div>
										<p className="text-xs text-muted-foreground">{search.description}</p>
										{search.parameters && (
											<div className="flex flex-wrap gap-1 mt-2">
												{Object.entries(search.parameters).map(([key, val]: [string, any]) => {
													if (!val || (typeof val === 'string' && !val.trim())) return null;
													const display = Array.isArray(val) ? val.join(', ') : String(val);
													return (
														<Badge key={key} variant="outline" className="text-[10px] font-normal">
															<span className="font-semibold">{key}:</span>&nbsp;{display.length > 60 ? display.slice(0, 60) + '...' : display}
														</Badge>
													);
												})}
											</div>
										)}
									</div>
								))}
							</div>
						</div>
					)}

					{/* Execution Tips */}
					{content.executionTips && content.executionTips.length > 0 && (
						<div>
							<h3 className="text-sm font-semibold mb-2">Execution Tips</h3>
							<ul className="space-y-2">
								{content.executionTips.map((tip: any, i: number) => (
									<li key={i} className="bg-muted/30 p-3 rounded-md text-sm">
										<p className="font-medium">{tip.tip}</p>
										{tip.context && <p className="text-xs text-muted-foreground mt-1">{tip.context}</p>}
									</li>
								))}
							</ul>
						</div>
					)}

					{/* Pipeline Cleaning Filters */}
					{content.pipelineCleaningFilters && (
						<div className="bg-destructive/5 border border-destructive/20 rounded-md p-3">
							<h3 className="text-sm font-semibold mb-2">Pipeline Cleaning Filters</h3>
							<div className="text-xs space-y-1">
								{content.pipelineCleaningFilters.excludeTitles && (
									<p><strong>Exclude Titles:</strong> {content.pipelineCleaningFilters.excludeTitles}</p>
								)}
								{content.pipelineCleaningFilters.excludeCompanies && (
									<p><strong>Exclude Companies:</strong> {content.pipelineCleaningFilters.excludeCompanies}</p>
								)}
							</div>
						</div>
					)}
				</div>
			);
		}

		// Handle rawContent fallback
		if (content.rawContent && typeof content.rawContent === 'string') {
			try {
				// Try to clean and parse the raw content
				let cleanJson = content.rawContent.trim();
				if (cleanJson.includes('```')) {
					cleanJson = cleanJson.replace(/```json\n?|\n?```/g, '').trim();
				}
				const firstBrace = cleanJson.indexOf('{');
				const lastBrace = cleanJson.lastIndexOf('}');
				if (firstBrace !== -1 && lastBrace !== -1) {
					cleanJson = cleanJson.substring(firstBrace, lastBrace + 1);
				}
				const parsed = JSON.parse(cleanJson);
				// If parsing succeeded, generic render of the parsed object for now if it didn't match specific types
				return (
					<pre className="whitespace-pre-wrap bg-muted p-4 rounded-md text-sm overflow-auto max-h-[500px]">
						{JSON.stringify(parsed, null, 2)}
					</pre>
				);
			} catch (e) {
				// If parsing fails, render as markdown (AI often returns markdown directly)
				return (
					<div className="prose prose-sm max-w-none dark:prose-invert prose-table:w-full prose-table:border-collapse prose-th:border prose-th:border-border prose-th:px-3 prose-th:py-2 prose-th:bg-muted prose-th:text-left prose-td:border prose-td:border-border prose-td:px-3 prose-td:py-2">
						<ReactMarkdown remarkPlugins={[remarkGfm]}>{stripCodeFences(content.rawContent)}</ReactMarkdown>
					</div>
				);
			}
		}

		// Check if it has fullText (for JDs) — render as markdown
		if (content.fullText) {
			return (
				<div className="prose prose-sm max-w-none dark:prose-invert prose-table:w-full prose-table:border-collapse prose-th:border prose-th:border-border prose-th:px-3 prose-th:py-2 prose-th:bg-muted prose-th:text-left prose-td:border prose-td:border-border prose-td:px-3 prose-td:py-2">
					<ReactMarkdown remarkPlugins={[remarkGfm]}>{stripCodeFences(content.fullText as string)}</ReactMarkdown>
				</div>
			);
		}

		// Render as formatted JSON for complex structures
		return (
			<div className="space-y-4">
				<pre className="whitespace-pre-wrap bg-muted p-4 rounded-md text-sm overflow-auto max-h-[500px]">
					{JSON.stringify(content, null, 2)}
				</pre>
			</div>
		);
	};

	return (
		<Card className="w-full">
			<CardHeader
				className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 space-y-0 cursor-pointer hover:bg-muted/50 transition-colors"
				onClick={() => setIsExpanded(!isExpanded)}
			>
				<div className="space-y-1 min-w-0">
					<CardTitle className="text-lg font-semibold truncate">{title}</CardTitle>
					<CardDescription>{description}</CardDescription>
				</div>
				<div className="flex items-center gap-1 flex-wrap" onClick={(e) => e.stopPropagation()}>
					{hasContent && !isEditing && (
						<>
							<Button variant="ghost" size="icon" onClick={handleEdit} title="Edit">
								<Pen className="h-4 w-4" />
							</Button>
							<Button
								variant="ghost"
								size="icon"
								onClick={() => {
									if (!showContextInput) {
										setContextText(module?.additionalContext || '');
									}
									setIsExpanded(true);
									setShowContextInput(!showContextInput);
								}}
								disabled={isGenerating}
								title={isGenerating ? 'Regenerating...' : 'Regenerate'}
								className={showContextInput ? 'text-primary' : ''}
							>
								<MessageSquare className="h-4 w-4" />
							</Button>
						</>
					)}
					{canDownloadPDF && !isEditing && (
						<Button
							variant="ghost"
							size="icon"
							onClick={(e) => { e.stopPropagation(); handleDownloadPDF(); }}
							disabled={isDownloadingPDF}
							title={isDownloadingPDF ? 'Downloading...' : 'Download PDF'}
						>
							<Download className="h-4 w-4" />
						</Button>
					)}
					<Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); onRefresh(); }} disabled={isLoading} title="Refresh">
						<RefreshCw className="h-4 w-4" />
					</Button>
					{module && (
						<Badge variant="secondary" className="text-xs ml-1">
							v{module.version} &middot; {module.source === 'ai' ? 'AI' : module.source === 'hybrid' ? 'Hybrid' : 'Manual'}
						</Badge>
					)}
					{isExpanded ? <ChevronUp className="h-5 w-5 text-muted-foreground ml-1 shrink-0" /> : <ChevronDown className="h-5 w-5 text-muted-foreground ml-1 shrink-0" />}
				</div>
			</CardHeader>
			{isExpanded && (
				<CardContent>
					{/* Context input for regeneration */}
					{showContextInput && (
						<div className="mb-4 border border-primary/30 rounded-lg p-4 bg-primary/5">
							{/* Meeting Notes textarea — only for deal_briefing */}
							{moduleType === 'deal_briefing' && (
								<div className="mb-4">
									<div className="flex items-center gap-2 mb-2">
										<MessageSquare className="h-4 w-4 text-primary" />
										<span className="text-sm font-medium">Meeting Notes</span>
									</div>
									<Textarea
										placeholder="Paste updated meeting notes from the intake/briefing session..."
										value={regenMeetingNotes}
										onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setRegenMeetingNotes(e.target.value)}
										rows={4}
										className="mb-2 text-sm"
									/>
									<p className="text-xs text-muted-foreground">Updated notes will be saved and used for Deal Briefing Section 1.</p>
								</div>
							)}
							<div className="flex items-center gap-2 mb-2">
								<MessageSquare className="h-4 w-4 text-primary" />
								<span className="text-sm font-medium">Add context for regeneration</span>
							</div>
							<Textarea
								placeholder="e.g. 'Focus on fintech companies', 'Add more unconventional searches targeting Go developers', 'Prioritize candidates with startup experience'..."
								value={contextText}
								onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setContextText(e.target.value)}
								rows={3}
								className="mb-3 text-sm"
							/>
							<div className="flex flex-wrap items-center gap-2">
								<Button
									size="sm"
									onClick={() => handleGenerate(contextText)}
									disabled={isGenerating}
								>
									<Send className="mr-1 h-3.5 w-3.5" />
									{isGenerating ? 'Regenerating...' : 'Regenerate with context'}
								</Button>
								<Button
									variant="ghost"
									size="sm"
									onClick={() => handleGenerate()}
									disabled={isGenerating}
								>
									Regenerate without context
								</Button>
								<Button
									variant="ghost"
									size="sm"
									onClick={() => { setShowContextInput(false); setContextText(''); }}
								>
									<X className="mr-1 h-3.5 w-3.5" /> Cancel
								</Button>
							</div>
						</div>
					)}

					<div className="flex justify-end mb-2">
						<Dialog open={showHistory} onOpenChange={(open) => {
							setShowHistory(open);
							if (open) fetchHistory();
						}}>
							<DialogTrigger asChild>
								<Button variant="ghost" size="sm" className="h-6 text-xs text-muted-foreground">
									<History className="mr-1 h-3 w-3" /> History
								</Button>
							</DialogTrigger>
							<DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
								<DialogHeader>
									<DialogTitle>Version History: {title}</DialogTitle>
								</DialogHeader>
								<div className="space-y-4 py-4">
									{isLoadingHistory ? (
										<div className="text-center py-4">Loading history...</div>
									) : history.length === 0 ? (
										<div className="text-center text-muted-foreground py-4">No history available</div>
									) : (
										<div className="space-y-4">
											{history.map((item: any) => (
												<div key={item.id} className="border rounded-lg p-4 flex flex-col gap-2">
													<div className="flex justify-between items-start">
														<div>
															<div className="flex items-center gap-2">
																<Badge variant="outline">v{item.version}</Badge>
																<span className="text-xs text-muted-foreground">
																	{item.createdAt ? format(new Date(item.createdAt), 'PPP p') : 'Unknown date'}
																</span>
															</div>
															<div className="text-xs text-muted-foreground mt-1">
																Source: {item.source} • By: {item.createdBy?.name || 'System'}
															</div>
														</div>
														<Button size="sm" variant="secondary" onClick={() => handleRestore(item)}>
															<RotateCcw className="mr-2 h-3 w-3" /> Restore
														</Button>
													</div>
													{item.readableContent && (
														<div className="mt-2 text-xs text-muted-foreground line-clamp-3 bg-muted p-2 rounded">
															{item.readableContent.substring(0, 150)}...
														</div>
													)}
													{item.additionalContext && (
														<div className="mt-2 text-xs bg-primary/5 border border-primary/20 p-2 rounded">
															<span className="font-medium text-primary">Context:</span> {item.additionalContext}
														</div>
													)}
												</div>
											))}
										</div>
									)}
								</div>
							</DialogContent>
						</Dialog>
					</div>

					{isLoading ? (
						<div className="flex items-center justify-center py-8">
							<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
						</div>
					) : (
						renderContent()
					)}
				</CardContent>
			)}
		</Card>
	);
}

export { MODULE_TITLES };
